"use client";

import { useEffect, useState, useRef } from "react";

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  updateInterval?: number;
  incrementAmount?: number;
  formatFn?: (value: number) => string;
}

export function AnimatedCounter({
  value,
  duration = 2000,
  updateInterval = 3000,
  incrementAmount = 0.5,
  formatFn = (val) => val.toLocaleString()
}: AnimatedCounterProps) {
  const [displayValue, setDisplayValue] = useState(value);
  const [animatedValue, setAnimatedValue] = useState(value);
  const lastUpdateTime = useRef(Date.now());
  const frameRef = useRef<number | null>(null);
  
  // Animation for smooth counting
  useEffect(() => {
    if (value !== displayValue) {
      setDisplayValue(value);
      
      const startValue = animatedValue;
      const startTime = Date.now();
      const endValue = value;
      
      const animateCount = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuad = (t: number) => t * (2 - t);
        const easedProgress = easeOutQuad(progress);
        
        const currentValue = startValue + (endValue - startValue) * easedProgress;
        setAnimatedValue(currentValue);
        
        if (progress < 1) {
          frameRef.current = requestAnimationFrame(animateCount);
        }
      };
      
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      
      frameRef.current = requestAnimationFrame(animateCount);
    }
    
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [value, displayValue, animatedValue, duration]);
  
  // Periodic updates to simulate mining
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();
      const timeDiff = now - lastUpdateTime.current;
      
      // Calculate how much to increment based on time passed
      const timeRatio = timeDiff / updateInterval;
      const increment = incrementAmount * timeRatio;
      
      setDisplayValue(prev => prev + increment);
      lastUpdateTime.current = now;
    }, updateInterval);
    
    return () => clearInterval(intervalId);
  }, [updateInterval, incrementAmount]);
  
  return (
    <span>{formatFn(Math.floor(animatedValue))}</span>
  );
}
